/**
 * Visual metadata for each notification category. Keeps the category-→icon and
 * category-→color mapping in one place so both the row icon, the left-rail
 * Lloji bucket, and any future surfaces (drawer header, badges) read from the
 * same source.
 *
 * Category values come from `app_notifications.category` (set during dispatch
 * from the event type). Unknown categories fall through to FALLBACK so the UI
 * never blanks out on a new event type that didn't get a mapping yet.
 */
export interface CategoryMeta {
    labelId: string;
    defaultLabel: string;
    color: string;       // background of the icon circle
    iconPath: string;    // single-path SVG (24x24 viewBox)
}

// SVG paths are 24x24, single-color via currentColor on the parent.
const ICON_PRODUCTION = 'M12 2l9 4.5v9L12 20l-9-4.5v-9L12 2zm0 2.18L5 7.5v8.5l7 3.32 7-3.32V7.5l-7-3.32zm0 3.32l5 2.36v4.96l-5 2.36-5-2.36V9.86L12 7.5z';
const ICON_INSTALLATION = 'M14.7 6.3a1 1 0 010 1.4L9.4 13l-3.1-3.1a1 1 0 011.4-1.4L9.4 10.2l4.9-3.9a1 1 0 011.4 0zM3 4h18v2H3V4zm0 14h18v2H3v-2zM3 11h6v2H3v-2zm12 0h6v2h-6v-2z';
const ICON_MEASURE = 'M14.84 4.22l-9.62 9.62 4.94 4.94 9.62-9.62-4.94-4.94zM5.22 13.84l9.62-9.62 1.41 1.41-1.06 1.06-.71-.71-1.06 1.06.71.71-.71.71-.71-.71-1.06 1.06.71.71-1.06 1.06-.71-.71-1.06 1.06.71.71-1.06 1.06z';
const ICON_OFFER = 'M5 8h14V6H5v2zm0 4h14v-2H5v2zm0 4h10v-2H5v2zm12 4l4-4-1.41-1.41L17 17.17V12h-2v5.17l-2.59-2.58L11 16l4 4z';
const ICON_TRANSPORT = 'M20 8h-3V4H3v13h2a3 3 0 006 0h4a3 3 0 006 0h2v-5l-3-4zM5 16V6h10v10H5zm3 1.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm9 0a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm-2-5h5l-2-3h-3v3z';
const ICON_SERVICE = 'M22.7 19l-9.1-9.1c.4-1.6.1-3.3-1.2-4.6a4.34 4.34 0 00-5-.8L9.7 7 8.3 8.4 5.6 5.7c-1 1.5-.7 3.6.7 5a4.18 4.18 0 004.6 1.2l9.1 9.1 2.7-2zm-3.4 1.4l-7.7-7.7c-.4.1-.7.2-1.1.2-1.1 0-2.2-.4-3-1.2-1-1-1.3-2.5-.8-3.8l2.3 2.3 2.8-2.8L9.5 5.1c1.3-.5 2.8-.2 3.8.8 1.1 1.1 1.4 2.7.7 4.1l7.7 7.7-2.4 2.7z';
const ICON_CONTACT = 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0a3 3 0 100-6 3 3 0 000 6zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05A4.8 4.8 0 0117 16.5V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z';
const ICON_DEFAULT = 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z';

export const CATEGORY_META: Record<string, CategoryMeta> = {
    production: {
        labelId: 'notifications.category.production',
        defaultLabel: 'Production',
        color: '#3b82f6',
        iconPath: ICON_PRODUCTION,
    },
    installation: {
        labelId: 'notifications.category.installation',
        defaultLabel: 'Installation',
        color: '#f97316',
        iconPath: ICON_INSTALLATION,
    },
    measure: {
        labelId: 'notifications.category.measure',
        defaultLabel: 'Measure',
        color: '#22c55e',
        iconPath: ICON_MEASURE,
    },
    offer: {
        labelId: 'notifications.category.offer',
        defaultLabel: 'Offer',
        color: '#a855f7',
        iconPath: ICON_OFFER,
    },
    transport: {
        labelId: 'notifications.category.transport',
        defaultLabel: 'Transport',
        color: '#06b6d4',
        iconPath: ICON_TRANSPORT,
    },
    service: {
        labelId: 'notifications.category.service',
        defaultLabel: 'Service',
        color: '#ef4444',
        iconPath: ICON_SERVICE,
    },
    servicing: {
        labelId: 'notifications.category.service',
        defaultLabel: 'Service',
        color: '#ef4444',
        iconPath: ICON_SERVICE,
    },
    contact: {
        labelId: 'notifications.category.contact',
        defaultLabel: 'Clients',
        color: '#64748b',
        iconPath: ICON_CONTACT,
    },
};

export const FALLBACK_CATEGORY: CategoryMeta = {
    labelId: 'notifications.category.other',
    defaultLabel: 'Other',
    color: '#94a3b8',
    iconPath: ICON_DEFAULT,
};

export function getCategoryMeta(category: string | null | undefined): CategoryMeta {
    if (!category) return FALLBACK_CATEGORY;
    return CATEGORY_META[category] ?? FALLBACK_CATEGORY;
}

// Display order for the Lloji sidebar bucket — matches the dashboard's
// module ordering so users see the same mental model across the app.
export const CATEGORY_ORDER: string[] = [
    'production', 'installation', 'measure', 'offer', 'transport', 'service', 'contact',
];
